import React, { useState, useEffect } from "react";
import LabelInput from "../../components/common/forms/Labelinput";
import Modal from "../../components/common/forms/modal";
import { API_CONFIG, getHeaders, refreshAccessToken } from "../../config/api";
import toast from "react-hot-toast";
import { useAuth } from "../../context/AuthContext"; // Auth context
import WarningModal from "../common/ui/warningmodel";

export default function AddMaterial({ isOpen, onClose, editData, onMaterialAdded }) {
  const [isProcessingService, setIsProcessingService] = useState(false);
  const [formData, setFormData] = useState({
    materialName: "",
    shortCode: "",
    hsnCode: "",
    basicDuty: "",
    socialDuty: "",
    Igst: ""
  });
  const [errors, setErrors] = useState({});
  const { logout } = useAuth();
  const [warningVisible, setWarningVisible] = useState(false);
  const [warningMessage, setWarningMessage] = useState("");

  useEffect(() => {
    if (editData) {
      setFormData({
        materialName: editData.materialName,
        shortCode: editData.shortCode,
        hsnCode: editData.hsnCode,
        basicDuty: editData.basicDuty,
        socialDuty: editData.socialDuty,
        Igst: editData.Igst
      });
    } else {
      setFormData({ materialName: "", shortCode: "", hsnCode: "", basicDuty: "", socialDuty: "", Igst: "" });
    }
  }, [editData]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    // if (name === "shortCode" && value.length > 2) return; // restrict to 2 chars
    setFormData({ ...formData, [name]: value });
  };


  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsProcessingService(true);
    setErrors({});

    try {
      const safeTrim = (v) =>
        v === undefined || v === null ? "" : String(v).trim();

      const payload = {
        name: safeTrim(formData.materialName),
        short_code: safeTrim(formData.shortCode),
        hsn_code: safeTrim(formData.hsnCode),
        basic_duty_pr: safeTrim(formData.basicDuty),
        social_duty_pr: safeTrim(formData.socialDuty),
        igst_pr: safeTrim(formData.Igst),
      };


      let response, result;

      const callApi = async () => {
        if (editData) {
          // 🟡 Edit Material API
          return await fetch(
            `${API_CONFIG.BASE_URL}${API_CONFIG.ENDPOINTS.MATERIAL.UPDATE_MATERIAL(editData.id)}`,
            {
              method: "PUT",
              headers: getHeaders(true),
              body: JSON.stringify(payload),
            }
          );
        } else {
          // 🟢 Add Material API
          return await fetch(
            `${API_CONFIG.BASE_URL}${API_CONFIG.ENDPOINTS.MATERIAL.CREATE}`,
            {
              method: "POST",
              headers: getHeaders(true),
              body: JSON.stringify(payload),
            }
          );
        }
      };

      // Initial API call
      response = await callApi();

      // -------------------------
      // 🔥 Handle 401 token expired
      // -------------------------
      if (response.status === 401) {
        console.warn("Access token expired. Attempting refresh...");

        const newToken = await refreshAccessToken();

        if (newToken) {
          // Retry API call with new token
          response = await callApi();
        } else {
          console.error("Refresh token failed. Logging out...");
          logout(); // redirect user
          return;
        }
      }

      result = await response.json();

      if (response.ok && (result.status === 200 || result.status === 201)) {
        toast.success(
          result.message ||
          (editData ? "Material updated successfully!" : "Material added successfully!")
        );

        // Reset form and close modal
        setFormData({
          materialName: "",
          shortCode: "",
          hsnCode: "",
          basicDuty: "",
          socialDuty: "",
          Igst: "",
        });
        setErrors({});

        if (onMaterialAdded) await onMaterialAdded(); // Refresh parent list
        onClose();
      } else {
        setWarningMessage(result.message || "Failed to save material");
        setWarningVisible(true);
        setErrors({ api: result.message || "Failed to save material" });
      }
    } catch (error) {
      console.error("Error adding/updating material:", error);
      setWarningMessage("Something went wrong");
      setWarningVisible(true);
      setErrors({ api: "Something went wrong" });
    } finally {
      setIsProcessingService(false);
    }
  };



  if (!isOpen) return null;



  return (
    <>
      <Modal
        isOpen={isOpen}
        title={editData ? "Edit Material" : "Add New Material"}
        onClose={onClose}
        onSubmit={handleSubmit}
        isProcessing={isProcessingService}
        submitText={editData ? "Update Material" : "Add Material"}
      >
        {/* First row */}
        <div className="flex gap-4">
          <LabelInput
            label="Material Name"
            type="text"
            name="materialName"
            value={formData.materialName}
            onChange={handleChange}
            placeholder="Enter Material Name"
            required
            error={errors.materialName}
            className="flex-1"
          />
          <LabelInput
            label="Short Code"
            type="text"
            name="shortCode"
            value={formData.shortCode}
            onChange={handleChange}
            placeholder="Enter Short Code"
            required
            error={errors.shortCode}
            className="flex-1"
          />

        </div>

        {/* Second row */}
        <div className="flex gap-4 mt-4">
          <LabelInput
            label="HSN Code"
            type="text"
            name="hsnCode"
            value={formData.hsnCode}
            onChange={handleChange}
            placeholder="Enter HSN Code"
            required
            error={errors.hsnCode}
            className="flex-1"
          />
          <LabelInput
            label="Basic Duty (%)"
            type="text"
            name="basicDuty"
            value={formData.basicDuty}
            onChange={handleChange}
            placeholder="Enter Basic Duty"
            required
            error={errors.basicDuty}
            className="flex-1"
          />
        </div>

        {/* Third row */}
        <div className="flex gap-4 mt-4">
          <LabelInput
            label="Social Duty (%)"
            type="text"
            name="socialDuty"
            value={formData.socialDuty}
            onChange={handleChange}
            placeholder="Enter Social Duty"
            required
            error={errors.socialDuty}
            className="flex-1"
          />
          <LabelInput
            label="IGST (%)"
            type="text"
            name="Igst"
            value={formData.Igst}
            onChange={handleChange}
            placeholder="Enter IGST"
            required
            error={errors.Igst}
            className="flex-1"
          />
        </div>
      </Modal>
      {warningVisible && (
        <WarningModal
          message={warningMessage}
          onClose={() => setWarningVisible(false)}
        />
      )}

    </>
  );
}
